import React, { useState, useEffect } from 'react';
import {
  Card,
  Descriptions,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  message,
  Divider,
  Row,
  Col,
} from 'antd';
import {
  MailOutlined,
  MobileOutlined,
  WechatOutlined,
  LockOutlined,
  LinkOutlined,
} from '@ant-design/icons';
import { useAuthStore } from '@/store/auth';
import { authApi } from '@/services/api';
import { useNavigate } from 'react-router-dom';
import SessionsModal from './SessionsModal';

const AccountSettings: React.FC = () => {
  const { user, setUser } = useAuthStore();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  // Modal states
  const [bindPhoneVisible, setBindPhoneVisible] = useState(false);
  const [bindEmailVisible, setBindEmailVisible] = useState(false);
  const [changePasswordVisible, setChangePasswordVisible] = useState(false);
  const [sessionsVisible, setSessionsVisible] = useState(false);

  // Forms
  const [bindPhoneForm] = Form.useForm();
  const [bindEmailForm] = Form.useForm();
  const [changePasswordForm] = Form.useForm();

  // Verification code countdown
  const [emailCountdown, setEmailCountdown] = useState(0);
  const [phoneCountdown, setPhoneCountdown] = useState(0);

  // Countdown timers
  useEffect(() => {
    if (emailCountdown > 0) {
      const timer = setTimeout(() => setEmailCountdown(emailCountdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [emailCountdown]);

  useEffect(() => {
    if (phoneCountdown > 0) {
      const timer = setTimeout(() => setPhoneCountdown(phoneCountdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [phoneCountdown]);

  // Send email verification code
  const handleSendEmailCode = async () => {
    const email = bindEmailForm.getFieldValue('email');
    if (!email) {
      message.error('请先输入邮箱地址');
      return;
    }

    try {
      const response = await authApi.sendVerificationCode('email', email, 'bind');
      if (import.meta.env.DEV && response.data.code) {
        message.success(`验证码已发送！开发模式：${response.data.code}`, 10);
      } else {
        message.success('验证码已发送到您的邮箱');
      }
      setEmailCountdown(60);
    } catch (error: any) {
      message.error(error.response?.data?.detail || '发送失败');
    }
  };

  // Send phone verification code
  const handleSendPhoneCode = async () => {
    const phone = bindPhoneForm.getFieldValue('phone');
    if (!phone) {
      message.error('请先输入手机号');
      return;
    }

    try {
      const response = await authApi.sendVerificationCode('phone', phone, 'bind');
      if (import.meta.env.DEV && response.data.code) {
        message.success(`验证码已发送！开发模式：${response.data.code}`, 10);
      } else {
        message.success('验证码已发送到您的手机');
      }
      setPhoneCountdown(60);
    } catch (error: any) {
      message.error(error.response?.data?.detail || '发送失败');
    }
  };

  // Bind phone
  const handleBindPhone = async (values: { phone: string; code: string }) => {
    setLoading(true);
    try {
      await authApi.bindPhone(values.phone, values.code);
      message.success('手机号绑定成功');
      setBindPhoneVisible(false);
      bindPhoneForm.resetFields();

      // Update user data
      if (user) {
        setUser({ ...user, phone: values.phone });
      }
    } catch (error: any) {
      message.error(error.response?.data?.detail || '绑定失败');
    } finally {
      setLoading(false);
    }
  };

  // Bind email
  const handleBindEmail = async (values: { email: string; code: string }) => {
    setLoading(true);
    try {
      await authApi.bindEmail(values.email, values.code);
      message.success('邮箱绑定成功');
      setBindEmailVisible(false);
      bindEmailForm.resetFields();

      // Update user data
      if (user) {
        setUser({ ...user, email: values.email });
      }
    } catch (error: any) {
      message.error(error.response?.data?.detail || '绑定失败');
    } finally {
      setLoading(false);
    }
  };

  // Unbind email
  const handleUnbindEmail = () => {
    Modal.confirm({
      title: '确认解绑邮箱？',
      content: '解绑后将无法使用邮箱登录',
      okText: '确认',
      cancelText: '取消',
      onOk: async () => {
        const password = await new Promise<string>((resolve) => {
          let pwd = '';
          Modal.confirm({
            title: '请输入密码确认',
            content: <Input.Password onChange={(e) => (pwd = e.target.value)} placeholder="请输入密码" />,
            onOk: () => resolve(pwd),
          });
        });

        if (!password) {
          message.error('请输入密码');
          return;
        }

        try {
          await authApi.unbindEmail(password);
          message.success('邮箱解绑成功');
          if (user) {
            setUser({ ...user, email: undefined });
          }
        } catch (error: any) {
          message.error(error.response?.data?.detail || '解绑失败');
        }
      },
    });
  };

  // Unbind phone
  const handleUnbindPhone = () => {
    Modal.confirm({
      title: '确认解绑手机号？',
      content: '解绑后将无法使用手机号登录',
      okText: '确认',
      cancelText: '取消',
      onOk: async () => {
        const password = await new Promise<string>((resolve) => {
          let pwd = '';
          Modal.confirm({
            title: '请输入密码确认',
            content: <Input.Password onChange={(e) => (pwd = e.target.value)} placeholder="请输入密码" />,
            onOk: () => resolve(pwd),
          });
        });

        if (!password) {
          message.error('请输入密码');
          return;
        }

        try {
          await authApi.unbindPhone(password);
          message.success('手机号解绑成功');
          if (user) {
            setUser({ ...user, phone: undefined });
          }
        } catch (error: any) {
          message.error(error.response?.data?.detail || '解绑失败');
        }
      },
    });
  };

  // Change password
  const handleChangePassword = async (values: { old_password: string; new_password: string; confirm_password: string }) => {
    if (values.new_password !== values.confirm_password) {
      message.error('两次输入的密码不一致');
      return;
    }

    setLoading(true);
    try {
      await authApi.changePassword(values.old_password, values.new_password);
      message.success('密码修改成功，请重新登录');
      setChangePasswordVisible(false);
      changePasswordForm.resetFields();

      // Redirect to login after 2 seconds
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (error: any) {
      message.error(error.response?.data?.detail || '密码修改失败');
    } finally {
      setLoading(false);
    }
  };

  // Navigate to sessions management
  const handleManageSessions = () => {
    setSessionsVisible(true);
  };

  return (
    <div>
      <Row gutter={24}>
        <Col xs={24} lg={16}>
          <Card title="登录方式管理" className="mb-4">
            <Descriptions column={1} bordered>
              <Descriptions.Item label="用户名">
                {user?.username} <Tag color="green">✓ 主要</Tag>
              </Descriptions.Item>

              <Descriptions.Item label="邮箱">
                {user?.email ? (
                  <Space>
                    {user.email}
                    <Tag color="blue">✓ 已绑定</Tag>
                    <Button type="link" size="small" danger onClick={handleUnbindEmail}>
                      解绑
                    </Button>
                  </Space>
                ) : (
                  <Space>
                    <Tag>未绑定</Tag>
                    <Button
                      type="link"
                      size="small"
                      icon={<MailOutlined />}
                      onClick={() => setBindEmailVisible(true)}
                    >
                      绑定邮箱
                    </Button>
                  </Space>
                )}
              </Descriptions.Item>

              <Descriptions.Item label="手机号">
                {user?.phone ? (
                  <Space>
                    {user.phone}
                    <Tag color="blue">✓ 已绑定</Tag>
                    <Button type="link" size="small" danger onClick={handleUnbindPhone}>
                      解绑
                    </Button>
                  </Space>
                ) : (
                  <Space>
                    <Tag>未绑定</Tag>
                    <Button
                      type="link"
                      size="small"
                      icon={<MobileOutlined />}
                      onClick={() => setBindPhoneVisible(true)}
                    >
                      绑定手机号
                    </Button>
                  </Space>
                )}
              </Descriptions.Item>

              <Descriptions.Item label="微信">
                {user?.wechat_union_id ? (
                  <Space>
                    {user.wechat_nickname || '已绑定'}
                    <Tag color="green">✓ 已绑定</Tag>
                  </Space>
                ) : (
                  <Space>
                    <Tag>未绑定</Tag>
                    <Button type="link" size="small" icon={<WechatOutlined />} disabled>
                      绑定微信
                    </Button>
                  </Space>
                )}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          <Card title="安全设置">
            <Space direction="vertical" style={{ width: '100%' }}>
              <Button
                icon={<LockOutlined />}
                onClick={() => setChangePasswordVisible(true)}
                block
              >
                修改密码
              </Button>
              <Button icon={<LinkOutlined />} onClick={handleManageSessions} block>
                查看活跃会话
              </Button>
            </Space>
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Card title="安全提示">
            <Space direction="vertical" size="small">
              <div>
                <Tag color="blue">建议</Tag>
                绑定多种登录方式，提高账号安全性
              </div>
              <div>
                <Tag color="orange">提醒</Tag>
                定期修改密码，保护账号安全
              </div>
              <div>
                <Tag color="green">提示</Tag>
                绑定手机号后可使用验证码快速登录
              </div>
            </Space>
          </Card>
        </Col>
      </Row>

      {/* Bind Phone Modal */}
      <Modal
        title="绑定手机号"
        open={bindPhoneVisible}
        onCancel={() => {
          setBindPhoneVisible(false);
          bindPhoneForm.resetFields();
        }}
        footer={null}
      >
        <Form form={bindPhoneForm} onFinish={handleBindPhone} layout="vertical">
          <Form.Item
            name="phone"
            label="手机号"
            rules={[
              { required: true, message: '请输入手机号' },
              { pattern: /^1[3-9]\d{9}$/, message: '请输入有效的手机号' },
            ]}
          >
            <Input prefix={<MobileOutlined />} placeholder="请输入手机号" />
          </Form.Item>

          <Form.Item
            name="code"
            label="验证码"
            rules={[
              { required: true, message: '请输入验证码' },
              { len: 6, message: '验证码为6位数字' },
            ]}
          >
            <Input
              placeholder="请输入验证码"
              suffix={
                <Button
                  type="link"
                  disabled={phoneCountdown > 0}
                  onClick={handleSendPhoneCode}
                  style={{ padding: 0 }}
                >
                  {phoneCountdown > 0 ? `${phoneCountdown}秒后重发` : '发送验证码'}
                </Button>
              }
            />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading} block>
              确认绑定
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Bind Email Modal */}
      <Modal
        title="绑定邮箱"
        open={bindEmailVisible}
        onCancel={() => {
          setBindEmailVisible(false);
          bindEmailForm.resetFields();
        }}
        footer={null}
      >
        <Form form={bindEmailForm} onFinish={handleBindEmail} layout="vertical">
          <Form.Item
            name="email"
            label="邮箱地址"
            rules={[
              { required: true, message: '请输入邮箱地址' },
              { type: 'email', message: '请输入有效的邮箱地址' },
            ]}
          >
            <Input prefix={<MailOutlined />} placeholder="请输入邮箱地址" />
          </Form.Item>

          <Form.Item
            name="code"
            label="验证码"
            rules={[
              { required: true, message: '请输入验证码' },
              { len: 6, message: '验证码为6位数字' },
            ]}
          >
            <Input
              placeholder="请输入验证码"
              suffix={
                <Button
                  type="link"
                  disabled={emailCountdown > 0}
                  onClick={handleSendEmailCode}
                  style={{ padding: 0 }}
                >
                  {emailCountdown > 0 ? `${emailCountdown}秒后重发` : '发送验证码'}
                </Button>
              }
            />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading} block>
              确认绑定
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Change Password Modal */}
      <Modal
        title="修改密码"
        open={changePasswordVisible}
        onCancel={() => {
          setChangePasswordVisible(false);
          changePasswordForm.resetFields();
        }}
        footer={null}
      >
        <Form form={changePasswordForm} onFinish={handleChangePassword} layout="vertical">
          <Form.Item
            name="old_password"
            label="当前密码"
            rules={[{ required: true, message: '请输入当前密码' }]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder="请输入当前密码" />
          </Form.Item>

          <Form.Item
            name="new_password"
            label="新密码"
            rules={[
              { required: true, message: '请输入新密码' },
              { min: 6, message: '密码至少6个字符' },
            ]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder="请输入新密码" />
          </Form.Item>

          <Form.Item
            name="confirm_password"
            label="确认新密码"
            dependencies={['new_password']}
            rules={[
              { required: true, message: '请确认新密码' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('new_password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('两次输入的密码不一致'));
                },
              }),
            ]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder="请再次输入新密码" />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading} block>
              确认修改
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Sessions Management Modal */}
      <SessionsModal visible={sessionsVisible} onClose={() => setSessionsVisible(false)} />
    </div>
  );
};

export default AccountSettings;
